import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import { translateOrDefault } from "../../utils/translationUtils";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { OneNoteSourceSettingsApi } from "./OneNoteSourceSettings.Api";
import type {
  OneNoteSourceSettingsViewModel,
  OneNoteTagGroupState,
} from "./OneNoteSourceSettings.ViewModel.types";
import type {
  OneNoteConfiguration,
  OneNoteGroup,
  OneNoteLocation,
  OneNoteNotebook,
  OneNoteSection,
  OneNoteSectionGroup,
  OneNoteSite,
  OneNoteTagGroup,
  OneNoteTagGroupId,
} from "./OneNoteSourceSettings.types";

const NONE_OPTION = "__none";

const TAG_GROUPS: OneNoteTagGroup[] = [
  {
    id: "tasks",
    tags: [
      "to-do",
      "to-do-priority-1",
      "to-do-priority-2",
      "important",
      "critical",
      "question",
      "client-request",
    ],
  },
  {
    id: "followUp",
    tags: [
      "call-back",
      "send-in-email",
      "schedule-meeting",
      "discuss-with-manager",
      "discuss-with-person-a",
      "discuss-with-person-b",
      "remember-for-later",
    ],
  },
  {
    id: "reference",
    tags: [
      "idea",
      "definition",
      "highlight",
      "contact",
      "address",
      "phone-number",
      "web-site-to-visit",
      "password",
    ],
  },
  {
    id: "projects",
    tags: ["project-a", "project-b"],
  },
  {
    id: "personal",
    tags: [
      "movie-to-see",
      "book-to-read",
      "music-to-listen-to",
      "source-for-article",
      "remember-for-blog",
    ],
  },
];

const normalizeId = (value: unknown): string => String(value ?? "");

const getLocationId = (
  location: OneNoteLocation,
  group?: OneNoteGroup,
  site?: OneNoteSite,
): string | null => {
  switch (location) {
    case "Me":
      return "mytasks";
    case "Group":
      return normalizeId(group?.id) || null;
    case "SharePoint":
      return normalizeId(site?.id) || null;
    default:
      return null;
  }
};

const getErrorMessage = (error: unknown, defaultMessage: string): string => {
  if (
    typeof error === "object" &&
    error &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object"
  ) {
    const response = (error as { response?: { data?: unknown } }).response;
    const message =
      response &&
      typeof response.data === "object" &&
      response.data &&
      "error" in response.data
        ? (response.data as { error?: { message?: string } }).error?.message
        : undefined;
    if (message) {
      return `${defaultMessage}: ${message}`;
    }
  }

  return defaultMessage;
};

export const useOneNoteSourceSettingsViewModel = (
  model: SourceSettingsModel,
): OneNoteSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const translateRef = useRef(model.t);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [groups, setGroups] = useState<OneNoteGroup[]>([]);
  const [sites, setSites] = useState<OneNoteSite[]>([]);
  const [notebooks, setNotebooks] = useState<OneNoteNotebook[]>([]);
  const [sectionGroups, setSectionGroups] = useState<OneNoteSectionGroup[]>([]);
  const [sections, setSections] = useState<OneNoteSection[]>([]);
  const [siteQuery, setSiteQuery] = useState("");
  const [searchingSites, setSearchingSites] = useState(false);
  const [siteSearchFailed, setSiteSearchFailed] = useState(false);
  const [showNewConnectionMessage, setShowNewConnectionMessage] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [groupsLoaded, setGroupsLoaded] = useState(true);
  const [notebooksLoaded, setNotebooksLoaded] = useState(true);
  const [sectionGroupsLoaded, setSectionGroupsLoaded] = useState(true);
  const [sectionsLoaded, setSectionsLoaded] = useState(true);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    translateRef.current = model.t;
  }, [model.t]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() === SourceTypeIds.OneNote &&
    model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () => parseConnectionConfiguration<OneNoteConfiguration>(model.editingConnection.configuration),
    [model.editingConnection.configuration],
  );

  const location: OneNoteLocation = configuration.location ?? "Me";
  const selectedSections = configuration.sections ?? [];

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    const updates: Partial<OneNoteConfiguration> = {};
    let shouldUpdateConfig = false;
    let nextNewTaskDaysLimit: string | null = null;

    if (!configuration.location) {
      updates.location = "Me";
      shouldUpdateConfig = true;
    }

    if (!configuration.tags) {
      updates.tags = {};
      shouldUpdateConfig = true;
    }

    if (model.editingConnection.newTaskDaysLimit === "-1") {
      nextNewTaskDaysLimit = "90";
      setShowNewConnectionMessage(true);
    } else if (model.editingConnection.newTaskDaysLimit.trim() === "") {
      nextNewTaskDaysLimit = "0";
      setShowNewConnectionMessage(false);
    } else {
      setShowNewConnectionMessage(false);
    }

    if (shouldUpdateConfig) {
      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          location: cfg.location || updates.location,
          tags: cfg.tags ?? updates.tags,
        }),
        { isDefault: true },
      );
    }

    if (nextNewTaskDaysLimit !== null) {
      onUpdateRef.current(
        { newTaskDaysLimit: nextNewTaskDaysLimit },
        { isDefault: true },
      );
    }
  }, [configuration.location, configuration.tags, editingConnectionId, isVisible, model.editingConnection.newTaskDaysLimit]);

  useEffect(() => {
    const currentProfileIdentity =
      `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setSites([]);
    setNotebooks([]);
    setSectionGroups([]);
    setSections([]);

    updateConnectionConfiguration<OneNoteConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        site: undefined,
        notebook: undefined,
        group: undefined,
        sectionGroup: undefined,
        sections: [],
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadGroups = async (): Promise<void> => {
      if (!isVisible || location !== "Group" || !model.boardId || !editingConnectionId) {
        setGroups([]);
        setGroupsLoaded(true);
        return;
      }

      const oneNoteApi = sourceSettingsApiRef.current?.tryGetSourceApi<OneNoteSourceSettingsApi>(
        SourceTypeIds.OneNote,
      );
      if (!oneNoteApi) {
        setGroups([]);
        setGroupsLoaded(true);
        return;
      }

      setGroupsLoaded(false);
      setHasError(false);
      setErrorMessage("");
      try {
        setGroups(await oneNoteApi.getGroups(model.boardId, editingConnectionId));
      } catch {
        setGroups([]);
      } finally {
        setGroupsLoaded(true);
      }
    };

    void loadGroups();
  }, [editingConnectionId, isVisible, location, model.boardId]);

  useEffect(() => {
    const searchSites = async (): Promise<void> => {
      if (!isVisible || location !== "SharePoint" || !model.boardId || !editingConnectionId) {
        setSites([]);
        setSiteSearchFailed(false);
        return;
      }

      const term = siteQuery.trim();
      if (term.length < 2) {
        setSites([]);
        setSiteSearchFailed(false);
        return;
      }

      const oneNoteApi = sourceSettingsApiRef.current?.tryGetSourceApi<OneNoteSourceSettingsApi>(
        SourceTypeIds.OneNote,
      );
      if (!oneNoteApi) {
        setSites([]);
        return;
      }

      setSearchingSites(true);
      setSiteSearchFailed(false);
      setHasError(false);
      setErrorMessage("");
      try {
        setSites(await oneNoteApi.getSites(model.boardId, editingConnectionId, term));
      } catch {
        setSites([]);
        setSiteSearchFailed(true);
      } finally {
        setSearchingSites(false);
      }
    };

    const timeoutId = setTimeout(() => {
      void searchSites();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [editingConnectionId, isVisible, location, model.boardId, siteQuery]);

  const locationId = getLocationId(location, configuration.group, configuration.site);

  useEffect(() => {
    const loadNotebooks = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId || !locationId) {
        setNotebooks([]);
        setNotebooksLoaded(true);
        return;
      }

      const oneNoteApi = sourceSettingsApiRef.current?.tryGetSourceApi<OneNoteSourceSettingsApi>(
        SourceTypeIds.OneNote,
      );
      if (!oneNoteApi) {
        setNotebooks([]);
        setNotebooksLoaded(true);
        return;
      }

      setNotebooksLoaded(false);
      setHasError(false);
      setErrorMessage("");
      try {
        setNotebooks(
          await oneNoteApi.getNotebooks(
            model.boardId,
            editingConnectionId,
            location,
            locationId,
          ),
        );
      } catch {
        setNotebooks([]);
      } finally {
        setNotebooksLoaded(true);
      }
    };

    void loadNotebooks();
  }, [editingConnectionId, isVisible, location, locationId, model.boardId]);

  useEffect(() => {
    const loadSectionGroups = async (): Promise<void> => {
      const notebookId = normalizeId(configuration.notebook?.id);
      if (!isVisible || !model.boardId || !editingConnectionId || !locationId || !notebookId) {
        setSectionGroups([]);
        setSectionGroupsLoaded(true);
        return;
      }

      const oneNoteApi = sourceSettingsApiRef.current?.tryGetSourceApi<OneNoteSourceSettingsApi>(
        SourceTypeIds.OneNote,
      );
      if (!oneNoteApi) {
        setSectionGroups([]);
        setSectionGroupsLoaded(true);
        return;
      }

      setSectionGroupsLoaded(false);
      setErrorMessage("");
      try {
        setSectionGroups(
          await oneNoteApi.getSectionGroups(
            model.boardId,
            editingConnectionId,
            location,
            locationId,
            notebookId,
          ),
        );
      } catch (error) {
        setSectionGroups([]);
        setHasError(true);
        setErrorMessage(getErrorMessage(
            error,
            translateOrDefault(
              translateRef.current,
              "board.connections.dialog.sourceSettings.onenote.loadSectionGroupsError",
              "Failed to load Section Groups",
            ),
          ));
      } finally {
        setSectionGroupsLoaded(true);
      }
    };

    void loadSectionGroups();
  }, [
    configuration.notebook?.id,
    editingConnectionId,
    isVisible,
    location,
    locationId,
    model.boardId,
  ]);

  useEffect(() => {
    const loadSections = async (): Promise<void> => {
      const notebookId = normalizeId(configuration.notebook?.id);
      if (!isVisible || !model.boardId || !editingConnectionId || !locationId || !notebookId) {
        setSections([]);
        setSectionsLoaded(true);
        return;
      }

      const sectionGroupId = normalizeId(configuration.sectionGroup?.id);
      const oneNoteApi = sourceSettingsApiRef.current?.tryGetSourceApi<OneNoteSourceSettingsApi>(
        SourceTypeIds.OneNote,
      );
      if (!oneNoteApi) {
        setSections([]);
        setSectionsLoaded(true);
        return;
      }

      setSectionsLoaded(false);
      setErrorMessage("");
      try {
        setSections(
          await oneNoteApi.getSections(
            model.boardId,
            editingConnectionId,
            location,
            locationId,
            notebookId,
            sectionGroupId,
          ),
        );
      } catch (error) {
        setSections([]);
        setHasError(true);
        setErrorMessage(getErrorMessage(
            error,
            translateOrDefault(
              translateRef.current,
              "board.connections.dialog.sourceSettings.onenote.loadSectionsError",
              "Failed to load Sections",
            ),
          ));
      } finally {
        setSectionsLoaded(true);
      }
    };

    void loadSections();
  }, [
    configuration.notebook?.id,
    configuration.sectionGroup?.id,
    editingConnectionId,
    isVisible,
    location,
    locationId,
    model.boardId,
  ]);

  const selectedGroupId = normalizeId(configuration.group?.id) || NONE_OPTION;
  const selectedSiteId = normalizeId(configuration.site?.id) || NONE_OPTION;
  const selectedNotebookId = normalizeId(configuration.notebook?.id) || NONE_OPTION;
  const selectedSectionGroupId = normalizeId(configuration.sectionGroup?.id) || NONE_OPTION;
  const selectedSectionIds = selectedSections.map((section) => normalizeId(section.id));
  const selectedTags = useMemo(() => configuration.tags ?? {}, [configuration.tags]);
  const tagGroups = useMemo<OneNoteTagGroupState[]>(
    () =>
      TAG_GROUPS.map((group) => {
        const selectedCount = group.tags.filter((tag) => selectedTags[tag]).length;
        return {
          ...group,
          selectedCount,
          isAllSelected: selectedCount === group.tags.length,
        };
      }),
    [selectedTags],
  );

  return {
    isVisible,
    showNewConnectionMessage,
    hasError,
    errorMessage,
    searchingSites,
    siteSearchFailed,
    groupsLoaded,
    notebooksLoaded,
    sectionGroupsLoaded,
    sectionsLoaded,
    location,
    groups,
    sites,
    notebooks,
    sectionGroups,
    sections,
    selectedGroupId,
    selectedSiteId,
    selectedSiteDisplayName: configuration.site?.displayName || "",
    selectedSiteWebUrl: configuration.site?.webUrl ?? "",
    selectedNotebookId,
    selectedSectionGroupId,
    selectedSectionIds,
    tagGroups,
    selectedTags,
    filterString: configuration.filterString ?? "",
    includeBulletedLists: Boolean(configuration.includeBulletedLists),
    includeTables: Boolean(configuration.includeTables),
    siteQuery,
    noneOptionValue: NONE_OPTION,
    onSelectLocation: (nextLocation: OneNoteLocation) => {
      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          location: nextLocation,
          group: undefined,
          site: undefined,
          notebook: undefined,
          sectionGroup: undefined,
          sections: [],
        }),
      );
      setGroups([]);
      setSites([]);
      setNotebooks([]);
      setSectionGroups([]);
      setSections([]);
      setSiteQuery("");
      setSiteSearchFailed(false);
    },
    onSiteQueryChange: (value: string) => {
      setSiteQuery(value);
    },
    onSelectGroup: (groupId?: string) => {
      const selectedId = String(groupId || NONE_OPTION);
      const selectedGroup = groups.find((group) => normalizeId(group.id) === selectedId);

      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          group: selectedId === NONE_OPTION ? undefined : selectedGroup,
          notebook: undefined,
          sectionGroup: undefined,
          sections: [],
        }),
      );
    },
    onSelectSite: (siteId?: string) => {
      const selectedId = String(siteId || NONE_OPTION);
      const selectedSite = sites.find((site) => normalizeId(site.id) === selectedId);

      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          site: selectedId === NONE_OPTION ? undefined : selectedSite,
          notebook: undefined,
          sectionGroup: undefined,
          sections: [],
        }),
      );
    },
    onSelectNotebook: (notebookId?: string) => {
      const selectedId = String(notebookId || NONE_OPTION);
      const selectedNotebook = notebooks.find(
        (notebook) => normalizeId(notebook.id) === selectedId,
      );

      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          notebook: selectedId === NONE_OPTION ? undefined : selectedNotebook,
          sectionGroup: undefined,
          sections: [],
        }),
      );
    },
    onSelectSectionGroup: (sectionGroupId?: string) => {
      const selectedId = String(sectionGroupId || NONE_OPTION);
      const selectedSectionGroup = sectionGroups.find(
        (sectionGroup) => normalizeId(sectionGroup.id) === selectedId,
      );

      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          sectionGroup:
            selectedId === NONE_OPTION ? undefined : selectedSectionGroup,
          sections: [],
        }),
      );
    },
    onSelectSections: (sectionIds: string[]) => {
      const selected = sections.filter((section) =>
        sectionIds.includes(normalizeId(section.id)),
      );

      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          sections: selected,
        }),
      );
    },
    onToggleTag: (tag: string, value: boolean) => {
      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          tags: {
            ...(cfg.tags ?? {}),
            [tag]: value,
          },
        }),
      );
    },
    onToggleTagGroup: (groupId: OneNoteTagGroupId, value: boolean) => {
      const group = TAG_GROUPS.find((candidate) => candidate.id === groupId);
      if (!group) {
        return;
      }

      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          tags: {
            ...(cfg.tags ?? {}),
            ...Object.fromEntries(group.tags.map((tag) => [tag, value])),
          },
        }),
      );
    },
    onFilterStringChange: (value: string) => {
      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          filterString: value,
        }),
      );
    },
    onToggleIncludeBulletedLists: (value: boolean) => {
      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          includeBulletedLists: value,
        }),
      );
    },
    onToggleIncludeTables: (value: boolean) => {
      updateConnectionConfiguration<OneNoteConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          includeTables: value,
        }),
      );
    },
  };
};
